import React from 'react';
import { cva } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

const cardVariants = cva(
  "rounded-lg overflow-hidden transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-white border border-neutral-200 shadow-sm hover:shadow-md",
        success: "bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 shadow-sm hover:shadow-md",
        danger: "bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 shadow-sm hover:shadow-md",
        info: "bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 shadow-sm hover:shadow-md",
        warning: "bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 shadow-sm hover:shadow-md",
        elevated: "bg-white shadow-md hover:shadow-lg",
        gradient: "bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-md hover:shadow-xl",
        outline: "border-2 border-neutral-300 bg-transparent hover:border-neutral-400",
      },
      padding: {
        none: "",
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
      },
      clickable: {
        true: "cursor-pointer hover:scale-[1.02] active:scale-[0.98]",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "md",
    },
  }
);

/**
 * Modern card component with multiple variants and animation support
 * @param {Object} props - Component props
 * @param {string} props.variant - Card variant (default, success, danger, info, warning, elevated, gradient, outline)
 * @param {string} props.padding - Card padding (none, sm, md, lg)
 * @param {boolean} props.clickable - Enable hover scale and click effects
 * @param {React.ReactNode} props.icon - Optional icon to display in header
 * @param {Function} props.onClick - Click handler function
 * @param {boolean} props.animated - Enable entrance animation
 * @param {React.ReactNode} props.children - Card content
 */
export const Card = React.forwardRef(
  ({ className, variant, padding, clickable, icon, onClick, animated = false, children, ...props }, ref) => {
    const Component = animated ? motion.div : 'div';
    const animationProps = animated ? {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.3 }
    } : {};

    return (
      <Component
        ref={ref}
        className={cn(cardVariants({ variant, padding, clickable: clickable || !!onClick, className }))}
        onClick={onClick}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick(e) : undefined}
        {...animationProps}
        {...props}
      >
        {icon && (
          <div className="flex items-center justify-center mb-4 text-3xl" aria-hidden="true">
            {icon}
          </div>
        )}
        {children}
      </Component>
    );
  }
);

Card.displayName = "Card";

/**
 * Card Header component
 */
export const CardHeader = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex flex-col space-y-1.5 pb-4", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardHeader.displayName = "CardHeader";

/**
 * Card Title component
 */
export const CardTitle = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={cn("text-lg md:text-xl font-bold leading-tight tracking-tight text-neutral-900", className)}
        {...props}
      >
        {children}
      </h3>
    );
  }
);

CardTitle.displayName = "CardTitle";

/**
 * Card Description component
 */
export const CardDescription = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn("text-sm text-neutral-500", className)}
        {...props}
      >
        {children}
      </p>
    );
  }
);

CardDescription.displayName = "CardDescription";

/**
 * Card Content component
 */
export const CardContent = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardContent.displayName = "CardContent";

/**
 * Card Footer component
 */
export const CardFooter = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-center pt-4", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = "CardFooter";
